import useRealtimeDraft from '../realtime/useRealtimeDraft';
import { usePermissions } from '../hooks/usePermissions';
import React, { useState, useEffect } from 'react';
import { Form, Button, Row, Col, Modal, Alert } from 'react-bootstrap';
import { useSelector, useDispatch } from 'react-redux';
import useUserDropdowns from '../functions/useUserDropdowns';
import useUserContact from '../functions/useUserContact';
import { extractEmailsFromProjectTeam } from '../functions/getUserEmailByRole';
import { selectGeneralProject, selectStationEnclosure, selectHcRequirements, selectTelemetry, selectSoldToShipTo } from '../redux/selectors';
import { selectApiUrls } from '../redux/slices/apiUrlSlice';
import { patchData } from '../redux/slices/apiCallSlice'; // Import patchData from apiCallSlice
import useSendEmail from '../functions/useSendEmail'; // Import the email sending hook
import { formatDateForForm } from '../utils/dateUtils'; // Import date formatting utility
import { mergeFunctionKeyHeaders } from '../axios/functionKey';
import '../styles.css'; // Assuming styles.css has the necessary styling

function JobReleaseForm() {
  const { canEdit } = usePermissions();
  const [realtimeDirty, setRealtimeDirty] = useState(false);
  useRealtimeDraft(realtimeDirty);
  // Get data from Redux using selectors
  const dispatch = useDispatch(); // Add dispatch hook
  const generalProjectData = useSelector(selectGeneralProject)[0] || {};
  const soldToShipToData = useSelector(selectSoldToShipTo)[0] || {}; // Add [0] to get the first item if it's an array
  const enclosureData = useSelector(selectStationEnclosure)[0] || {};
  const hcData = useSelector(selectHcRequirements)[0] || {};
  const telemetryData = useSelector(selectTelemetry)[0] || {};
  const currentUser = useSelector((state) => state.user?.userDetails?.name || '');
  const userEmail = useSelector((state) => state.user?.userDetails?.username || ''); // Get current user's email
  const apiUrls = useSelector(selectApiUrls); // Get API URLs from Redux
  
  // Import the email sending hook
  const sendEmail = useSendEmail();

  const includeCSM = ((enclosureData?.HasEnclosure && enclosureData?.EnclosureType === 'CSM') || (hcData?.SuctionLinesBy === 'Watertronics' && hcData?.SuctionLineMaterial === 'HDPE')) ? 'Yes' : 'No';
  const hasTelemetry = telemetryData?.HasTelemetry || false;

  // Get engineer options from the hook
  const { mechOptions, elecOptions, controlOptions, loading } = useUserDropdowns();

  // User contact data for project team emails
  const { userContacts, loading: contactsLoading, error: contactsError } = useUserContact();
  const [projectTeamMembers, setProjectTeamMembers] = useState([]);

  // State for form data
  const [formData, setFormData] = useState({
    jobName: '',
    jobNum: '',
    modelNumber: '',
    releasedBy: '',
    salesName: '',
    mechEngineer: '',
    elecEngineer: '',
    controlEngineer: '',
    release: '',
    CSOtype: '',
    exc1: false,
    exc2: false,
    exc3: false,
    exc4: false,
    exc5: false,
    ER1: false,
    ER2: false,
    ER3: false,
    ERother: '',
    LLR1: false,
    LLR2: false,
    LLRother: '',
    CSOdate: '',
    manAuthRelease: '',
    CSM: includeCSM,
    leadTime: 'Standard Lead Time',
    deliveryDate: '',
    notes: '',
    telemetry: (hasTelemetry ? 'YesWV' : 'No'),
    addEmails: '',
    sendToSelfOnly: false, // New checkbox state
  });

  // State for additional sales and engineers
  const [additionalSales, setAdditionalSales] = useState([]);
  const [additionalEngs, setAdditionalEngs] = useState([]);
  
  // State for email validation
  const [emailError, setEmailError] = useState('');

  // Email validation function
  const validateEmails = (emailString) => {
    if (!emailString || emailString.trim() === '') {
      setEmailError('');
      return true;
    }

    // Split by semicolon, comma, or space
    const emails = emailString
      .split(/[;,\s]+/)
      .map(email => email.trim())
      .filter(email => email);

    // Email regex pattern
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    // Check each email
    const invalidEmails = emails.filter(email => !emailRegex.test(email));

    if (invalidEmails.length > 0) {
      setEmailError(`Invalid email(s): ${invalidEmails.join(', ')}`);
      return false;
    }

    setEmailError('');
    return true;
  };

  // State for showing/hiding different sections
  const [showExceptions, setShowExceptions] = useState(false);
  const [showCSODiv, setShowCSODiv] = useState(false);
  const [showCSOAnticipation, setShowCSOAnticipation] = useState(false);
  const [showERDiv, setShowERDiv] = useState(false);
  const [showLLRDiv, setShowLLRDiv] = useState(false);
  const [showAuthorize, setShowAuthorize] = useState(false);
  const [showRTUMessage, setShowRTUMessage] = useState(false);
  const [showWVMessage, setShowWVMessage] = useState(false);
  const [showDefinitionsModal, setShowDefinitionsModal] = useState(false);

  // Initialize form with data from Redux when it's available
  useEffect(() => {
    if (generalProjectData) {
      // Format the delivery date to remove time component if it exists
      let formattedDeliveryDate = '';
      if (generalProjectData.RequestedDeliveryDate) {
        const date = new Date(generalProjectData.RequestedDeliveryDate);
        if (!isNaN(date)) {
          formattedDeliveryDate = date.toISOString().split('T')[0]; // Format as YYYY-MM-DD
        } else {
          formattedDeliveryDate = generalProjectData.RequestedDeliveryDate;
        }
      }

      setFormData(prevState => ({
        ...prevState,
        jobName: generalProjectData.ProjectName || '',
        jobNum: generalProjectData.JobNumber || '',
        modelNumber: generalProjectData.ModelNumber || '',
        releasedBy: currentUser || '',
        salesName: generalProjectData.SalesRep || '',
        mechEngineer: '',
        elecEngineer: '',
        controlEngineer: '',
        deliveryDate: formattedDeliveryDate,
      }));
    }
  }, [generalProjectData, currentUser]);

  // Function to extract project team member emails from JobReleaseForm's formData
  const extractProjectTeamFromFormData = (userContacts, formData, additionalEngs) => {
    if (!userContacts) return [];

    const projectTeamMembers = [];
    
    // Add Project Manager if available
    const projectManagerName = generalProjectData.ProjectManager;
    if (projectManagerName && userContacts.projectManager) {
      const pmContact = userContacts.projectManager.find(contact => contact.name === projectManagerName);
      if (pmContact) {
        projectTeamMembers.push({
          role: 'Project Manager',
          displayName: pmContact.name,
          email: pmContact.email
        });
      }
    }
    
    // Add Sales Rep if available
    const salesRepName = formData.salesName;
    if (salesRepName && userContacts.sales) {
      const salesContact = userContacts.sales.find(contact => contact.name === salesRepName);
      if (salesContact) {
        projectTeamMembers.push({
          role: 'Sales Representative',
          displayName: salesContact.name,
          email: salesContact.email
        });
      }
    }
    
    // Map engineer fields to their corresponding user contact keys
    const engineerMappings = [
      { formField: 'mechEngineer', contactKey: 'mechanicalEngineer', role: 'Mechanical Engineer' },
      { formField: 'elecEngineer', contactKey: 'electricalEngineer', role: 'Electrical Engineer' },
      { formField: 'controlEngineer', contactKey: 'controlsEngineer', role: 'Controls Engineer' }
    ];

    // Check each engineer field in formData
    engineerMappings.forEach(({ formField, contactKey, role }) => {
      const engineerName = formData[formField];
      if (engineerName && engineerName !== "") {
        const contactList = userContacts[contactKey] || [];
        const matchedContact = contactList.find(contact => contact.name === engineerName);
        if (matchedContact) {
          projectTeamMembers.push({
            role: role,
            displayName: matchedContact.name,
            email: matchedContact.email
          });
        }
      }
    });

    // Handle additional engineers (these don't have specific roles)
    if (additionalEngs && additionalEngs.length > 0) {
      additionalEngs.forEach((engineerName, index) => {
        if (engineerName && engineerName.trim() !== "") {
          // Try to find this engineer in any of the contact lists
          let matchedContact = null;
          ['mechanicalEngineer', 'electricalEngineer', 'controlsEngineer'].forEach(contactKey => {
            if (!matchedContact) {
              const contactList = userContacts[contactKey] || [];
              matchedContact = contactList.find(contact => contact.name === engineerName.trim());
            }
          });

          if (matchedContact) {
            projectTeamMembers.push({
              role: `Additional Engineer ${index + 1}`,
              displayName: matchedContact.name,
              email: matchedContact.email
            });
          }
        }
      });
    }

    return projectTeamMembers;
  };
  // Update project team members when user contacts or form data changes
  useEffect(() => {
    if (userContacts) {
      const teamMembers = extractProjectTeamFromFormData(userContacts, formData, additionalEngs);
      setProjectTeamMembers(teamMembers);
    }
  }, [userContacts, formData.mechEngineer, formData.elecEngineer, formData.controlEngineer, additionalEngs, formData]);

  // Handle form input changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });

    // Validate emails when addEmails field changes
    if (name === 'addEmails') {
      validateEmails(value);
    }

    // Handle specific field changes
    if (name === 'exc1') {
      setShowRTUMessage(checked);
    } else if (name === 'exc5') {
      setShowWVMessage(checked);
    } else if (name === 'ER2') {
      // Handle ER2 checkbox for showing/hiding ERother textarea
    } else if (name === 'LLR2') {
      // Handle LLR2 checkbox for showing/hiding LLRother textarea
    }
  };

  // Handle release type radio buttons
  const handleReleaseChange = (e) => {
    const { value } = e.target;
    setFormData({
      ...formData,
      release: value,
    });

    // Hide all sections first
    setShowExceptions(false);
    setShowCSODiv(false);
    setShowERDiv(false);
    setShowLLRDiv(false);
    setShowAuthorize(false);
    setShowCSOAnticipation(false);

    // Show appropriate sections based on selection
    if (value === 'Full Release with Qualified Exceptions') {
      setShowExceptions(true);
    } else if (value === 'Full Release with CSO Pending') {
      setShowCSODiv(true);
    } else if (value === 'Engineering Release Only') {
      setShowERDiv(true);
      setShowAuthorize(true);
    } else if (value === 'Long Leads Release Only') {
      setShowLLRDiv(true);
      setShowAuthorize(true);
    }
  };

  // Handle CSO type radio buttons
  const handleCSOTypeChange = (e) => {
    const { value } = e.target;
    setFormData({
      ...formData,
      CSOtype: value,
    });
    setShowCSOAnticipation(true);
    setShowAuthorize(true);
  };

  // Helper function to format dates consistently
  const formatDateForInput = (dateString) => formatDateForForm(dateString);

  // Function to patch general project status
  const patchGeneralProjectStatus = async (releaseType, deliveryDate) => {
    try {
      const generalProjectUrl = apiUrls.generalProject;
      
      if (!generalProjectUrl) {
        console.error('General project API URL not found');
        throw new Error('General project API URL not found');
      }

      // Map release type to appropriate ProjectStatus and JobStatus values
      let projectStatus = '';
      let jobStatus = '';

      switch (releaseType) {
        case 'Full Release':
          projectStatus = 'Full Release';
          jobStatus = 'Ready to Schedule';
          break;
        case 'Full Release with Qualified Exceptions':
          projectStatus = 'Release with Qualified Exceptions';
          jobStatus = 'Release with Qualified Exceptions';
          break;
        case 'Full Release with CSO Pending':
          projectStatus = 'Release with CSO Pending';
          jobStatus = 'Release Pending CSO';
          break;
        case 'Engineering Release Only':
          projectStatus = 'Engineering Only';
          jobStatus = 'Engineering Only';
          break;
        case 'Long Leads Release Only':
          projectStatus = 'Long Leads Only';
          jobStatus = 'Long Lead Only';
          break;
        default:
          projectStatus = releaseType;
          jobStatus = releaseType;
      }
      
      console.log('soldToShipToData:', soldToShipToData);

      // Format the data object following the same structure as EnhancedJobScheduleTable.js
      const putData = {
        // Keep ID capitalized
        ID: generalProjectData.ID,
        ProjectStatus: projectStatus,
        JobLocation: soldToShipToData?.ShipToCity && soldToShipToData?.ShipToState 
          ? `${soldToShipToData.ShipToCity}, ${soldToShipToData.ShipToState}` 
          : '',
        DollarValue: String(soldToShipToData?.InvoicePrice || 0), // Use String() instead of string()
        JobStatus: jobStatus,
        RequestedDeliveryDate: deliveryDate || null,
        ProductionRelease: (formatDateForInput(new Date().toISOString()))
      };

      console.log('Patching general project with:', putData);
      console.log('Using URL:', `${generalProjectUrl}?id=${generalProjectData.ID}`);

      // Use Redux apiCallSlice for PATCH operation with retry logic and better error handling
      // Note: Timeout and retry logic handled by axiosConfig (progressive timeouts: 5s, 10s, 15s, 20s, 30s)
      const result = await dispatch(patchData({
        url: `${generalProjectUrl}?id=${generalProjectData.ID}`,
        data: putData
      })).unwrap();

      console.log('General project status updated successfully:', result);
      return result;
    } catch (error) {
      console.error('Error updating general project status:', error);
      console.error('Error details:', {
        message: error.message,
        status: error.status,
        data: error.data
      });
      // Re-throw the error so the calling function can handle it
      throw error;
    }
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!canEdit('portal.additional-items.release-form')) return;
    
    // Validate emails before submission
    if (!validateEmails(formData.addEmails)) {
      alert('Please fix the invalid email addresses before submitting.');
      return;
    }
    
    if (window.confirm('Are you sure you want to submit?')) {
      console.log('Form submitted:', formData);
      console.log('Additional Sales:', additionalSales);
      console.log('Additional Engineers:', additionalEngs);
      
      // Format and log the email body
      const formattedEmailBody = formatEmailBody();
      console.log('Formatted Email Body:');
      console.log(formattedEmailBody);
      
      // Prepare the data for the API call - mapping form fields to API fields
      const submitData = {
        ProjectID: parseInt(generalProjectData.ID) || 0, // Convert to integer
        JobName: formData.jobName,
        JobNumber: formData.jobNum,
        ModelNumber: formData.modelNumber,
        ReleasedBy: formData.releasedBy,
        Salesperson: [formData.salesName, ...additionalSales.filter(s => s.trim() !== '')].join(', '),
        MechanicalEngineer: formData.mechEngineer || '',
        ElectricalEngineer: formData.elecEngineer || '',
        ControlsEngineer: formData.controlEngineer || '',
        ReleaseType: formData.release || '',
        RTUTypeMissing: formData.exc1 || 'false',
        WetWellDepthMissing: formData.exc2 || 'false',
        ZPipeDetailsMissing: formData.exc3 || false,
        IntakeAdaptorMissing: formData.exc4 || 'false',
        WaterVisionCloudCustomerSiteInfoMissing: formData.exc5 || 'false',
        CSOPendingStatus: formData.CSOtype ? (formData.CSOtype === 'CSO1' ? 'Do not order long leads' : 'Only order special long leads') : '',
        CSOApprovalDate: formData.CSOdate || '',
        CSOMissing: formData.ER1 || formData.LLR1 || 'false',
        Other: formData.ER2 || formData.LLR2 || 'false',
        OtherComments: formData.ERother || formData.LLRother || '',
        LongLeadCanBeOrdered: formData.ER3 || 'false',
        ManagerAuthorizingEarlyRelease: formData.manAuthRelease ? formData.manAuthRelease.split('|')[0] : '',
        JobIncludesCSMHDPE: formData.CSM || 'No',
        LeadTime: formData.leadTime || '',
        RequestedDeliveryDate: formData.deliveryDate || '',
        Notes: formData.notes || '',
        JobIncludesTelemetry: formData.telemetry || 'No',
        
        // Additional non-DB fields for email functionality
        emailBody: formattedEmailBody,
        additionalEngineers: additionalEngs.filter(eng => eng.trim() !== ''),
        additionalEmails: formData.addEmails || ''
      };

      // Send the data to the backend
      const submitReleaseForm = async () => {
        try {
          const releaseFormUrl = apiUrls.releaseForm;
          
          if (!releaseFormUrl) {
            console.error('Release form API URL not found');
            alert('Error: API endpoint not configured. Please try again later or contact support.');
            return;
          }

          console.log('Submitting data to API:', submitData);

          const response = await fetch(releaseFormUrl, {
              method: 'POST',
              headers: mergeFunctionKeyHeaders({
                  'Content-Type': 'application/json',
                  'Access-Control-Allow-Origin': '*'
              }, releaseFormUrl),
              body: JSON.stringify(submitData)
          });

          // Check if the response is successful based on status code
          if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
          }

          // Clone the response before trying to read it
          const responseClone = response.clone();
          
          // Try to parse as JSON first
          try {
            const responseData = await response.json();
            console.log('Response from API:', responseData);
          } catch (jsonError) {
            // If parsing as JSON fails, get the text response from the cloned response
            const textResponse = await responseClone.text();
            console.log('Response from API (text):', textResponse);
          }
            
          // Now patch the generalProject table with the status values based on release type
          console.log('Updating job status in generalProject table...');
          try {
            await patchGeneralProjectStatus(formData.release, formData.deliveryDate);
            console.log('Job status updated successfully');
          } catch (patchError) {
            console.error('Error updating job status:', patchError);
            // This is critical - if status update fails, we need to know about it
            throw new Error(`Failed to update job status in generalProject table: ${patchError.message}`);
          }
            
          // Send the email with the formatted body
          try {
            // Create email subject
            const emailSubject = `${formData.jobNum} ${formData.jobName} - ${formData.release}`;
              // Process additional emails if any are provided
            const ccRecipients = [];
            if (formData.addEmails) {
              // Split by semicolon, comma, or space - trim whitespace, and filter out empty entries
              const additionalEmailsArray = formData.addEmails
                .split(/[;,\s]+/)
                .map(email => email.trim())
                .filter(email => email);
                
              // Add to CC list
              ccRecipients.push(...additionalEmailsArray);
            }
            
            // Add project team emails to CC recipients
            const projectTeamEmails = extractEmailsFromProjectTeam(projectTeamMembers);
            if (projectTeamEmails.length > 0) {
              ccRecipients.push(...projectTeamEmails);
            }
            // Send email
            // Prepare recipients
            let toRecipients;
            let finalCcRecipients = ccRecipients;
            if (formData.sendToSelfOnly) {
              // Only send to current user
              toRecipients = userEmail ? [userEmail] : [];
              finalCcRecipients = [];
            } else {
              toRecipients = ['ReleaseGroup@watertronics.com', 'david.neesen@watertronics.com', 'chandler.blaschka@watertronics.com'];
              // Add CSM team if job includes CSM/HDPE components
              if (formData.CSM === 'Yes') {
                toRecipients.push('NorthPrairieDL@watertronics.com');
              }
              
              // Add Factory Service if job includes WaterVision telemetry
              if (formData.telemetry === 'YesWV') {
                toRecipients.push('factoryservice@watertronics.com');
              }
            }
            
            await sendEmail({
              subject: emailSubject,
              body: formattedEmailBody, // Already formatted as HTML
              toRecipients: toRecipients,
              ccRecipients: finalCcRecipients,
              from: 'job.release@watertronics.com',
              importance: 'Normal'
            });
            
            console.log('Email sent successfully');
          } catch (emailError) {
            console.error('Error sending email:', emailError);
            // Don't fail the whole process if email sending fails
            // Continue to show success and reset form since the critical operations succeeded
            alert('Form submitted successfully and job status updated!\n\nHowever, there was an error sending the notification email. Please manually notify the release team about this job release.');
          }
          
          alert('Form submitted successfully! Job has been released to scheduling.');
          
          // Reset form after successful submission
          setRealtimeDirty(false);
          setFormData({
            jobName: '',
            jobNum: '',
            modelNumber: '',
            releasedBy: currentUser || '',
            salesName: '',
            mechEngineer: '',
            elecEngineer: '',
            controlEngineer: '',
            release: '',
            CSOtype: '',
            exc1: false,
            exc2: false,
            exc3: false,
            exc4: false,
            exc5: false,
            ER1: false,
            ER2: false,
            ER3: false,
            ERother: '',
            LLR1: false,
            LLR2: false,
            LLRother: '',
            CSOdate: '',
            manAuthRelease: '',
            CSM: includeCSM,
            leadTime: 'Standard Lead Time',
            deliveryDate: '',
            notes: '',
            telemetry: (hasTelemetry ? 'YesWV' : 'No'),
            addEmails: '',
          });
          setAdditionalSales([]);
          setAdditionalEngs([]);
        } catch (error) {
          console.error('Error submitting form:', error);
          
          // Provide more specific error messages
          if (error.message.includes('generalProject')) {
            alert(`Form data was saved, but failed to update job status: ${error.message}\n\nPlease contact the system administrator to manually update the job status.`);
          } else {
            alert(`Failed to submit form: ${error.message}\n\nPlease try again or contact the system administrator if the problem persists.`);
          }
        }
      };

      submitReleaseForm();
    }
  };
  // Function to format the email body
  const formatEmailBody = () => {
    // Get current date in YYYY-MM-DD format
    const today = new Date();
    const releaseDate = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    
    // Combine primary salesperson with additional salespeople
    let salespeople = formData.salesName;
    if (additionalSales.length > 0) {
      const filteredAdditionalSales = additionalSales.filter(sale => sale.trim() !== '');
      if (filteredAdditionalSales.length > 0) {
        salespeople += ', ' + filteredAdditionalSales.join(', ');
      }
    }
    
    // Create a list of assisting engineers if any are selected
    let assistingEngineers = [];
    if (formData.mechEngineer) assistingEngineers.push(formData.mechEngineer);
    if (formData.elecEngineer) assistingEngineers.push(formData.elecEngineer);
    if (formData.controlEngineer) assistingEngineers.push(formData.controlEngineer);
    if (additionalEngs.length > 0) {
      const filteredAdditionalEngs = additionalEngs.filter(eng => eng.trim() !== '');
      assistingEngineers = [...assistingEngineers, ...filteredAdditionalEngs];
    }
    
    // Format the engineers section if there are any
    let engineersSection = '';
    if (assistingEngineers.length > 0) {
      engineersSection = `<p><strong>Engineer(s) that Assisted on Job Proposal:</strong> ${assistingEngineers.join(', ')}</p>`;
    }
    
    // Build the release type section
    let releaseTypeSection = `<p><strong>Type of Release:</strong> ${formData.release}</p>`;
    
    // Add exceptions if applicable
    if (formData.release === 'Full Release with Qualified Exceptions') {
      const exceptions = [];
      if (formData.exc1) exceptions.push('RTU Type Missing');
      if (formData.exc2) exceptions.push('Wet Well Depth Missing');
      if (formData.exc3) exceptions.push('Z-Pipe Details Missing');
      if (formData.exc4) exceptions.push('Intake / Adaptor Missing');
      if (formData.exc5) exceptions.push('WaterVision-Cloud Customer / Site Info Missing');
      
      if (exceptions.length > 0) {
        releaseTypeSection += '<ul>';
        exceptions.forEach(exception => {
          releaseTypeSection += `<li>${exception}</li>`;
        });
        releaseTypeSection += '</ul>';
      }
    }    // Add CSO details if applicable
    if (formData.release === 'Full Release with CSO Pending') {
      releaseTypeSection += '<ul style="color: red;">';
      if (formData.CSOtype === 'CSO1') {
        releaseTypeSection += `<li><strong>Do not order long leads or hold pre-engineering release meeting until CSO approved.</strong> A status note shall be added to Jobs Schedule: CSO expected ${formData.CSOdate}; LL not ordered.</li>`;
      } else if (formData.CSOtype === 'CSO2') {
        releaseTypeSection += `<li><strong>Only order special long lead items identified in NOTES section of this form</strong>, with balance of long leads ordered after CSO approval and pre-engineering release meeting. A status note shall be added to Jobs Schedule: CSO expected ${formData.CSOdate}; special LL ordered.</li>`;
      }      // Add manager authorization if applicable
      if (formData.manAuthRelease) {
        const managerName = formData.manAuthRelease.split('|')[0];
        releaseTypeSection += `<li><strong>Manager Authorizing Early Release:</strong> ${managerName}</li>`;
      }
      
      releaseTypeSection += '</ul>';
    }
    
    // Add Engineering Release or Long Leads details if applicable
    if (formData.release === 'Engineering Release Only' || formData.release === 'Long Leads Release Only') {
      const reasons = [];
      if (formData.ER1 || formData.LLR1) reasons.push('CSO Missing');
      if ((formData.ER2 || formData.LLR2) && (formData.ERother || formData.LLRother)) {
        reasons.push(formData.ERother || formData.LLRother);
      }
      
      if (reasons.length > 0) {
        releaseTypeSection += '<ul>';
        reasons.forEach(reason => {
          releaseTypeSection += `<li>${reason}</li>`;
        });
        releaseTypeSection += '</ul>';
      }
      
      if (formData.manAuthRelease) {
        const managerName = formData.manAuthRelease.split('|')[0];
        releaseTypeSection += `<p><strong>Manager Authorizing Early Release:</strong> ${managerName}</p>`;
      }
    }
    
    // Build the telemetry section
    let telemetrySection = '<p>';
    if (formData.telemetry === 'YesWV') {
      telemetrySection += '<strong>Job includes WaterVision Telemetry</strong>';
    } else if (formData.telemetry === 'YesFN') {
      telemetrySection += '<strong>Job includes FieldNet Telemetry</strong>';
    } else {
      telemetrySection += '<strong>Job does not include Telemetry</strong>';
    }
    telemetrySection += '</p>';
    
    // Format notes with proper styling if provided
    const notesSection = formData.notes ? 
      `<p><strong>Notes:</strong></p><p>${formData.notes.replace(/\n/g, '<br/>')}</p>` : 
      '<p><strong>Notes:</strong> None</p>';
      // Construct the complete email body with HTML formatting
    const emailBody = `
    <div style="font-family: Arial, sans-serif; line-height: 1.5;">
   
      <div style="margin-bottom: 20px;">
      <strong>Job Name:</strong> ${formData.jobName}</br>
      <strong>Job Number:</strong> ${formData.jobNum}</br>
      <strong>Model Number:</strong> ${formData.modelNumber}</br>
      <strong>Released By:</strong> ${formData.releasedBy}</br>
      <strong>Release Date:</strong> ${releaseDate}</br>
      <strong>Salesperson(s) / Account(s) on Job:</strong> ${salespeople}</br>
      </br>
      <strong>Lead Time:</strong> ${formData.leadTime}</br>
      <strong>Requested Delivery Date:</strong> ${formData.deliveryDate || 'Not specified'}</br>
      </div>
      
      <div>
        ${engineersSection}
        
        ${releaseTypeSection}
        
        ${notesSection}
        
        ${telemetrySection}
      </div>
      
      <p style="margin-top: 30px; font-size: 0.8em; color: #666;">This is an automated message from the Watertronics Production Portal.</p>
    </div>`;

    return emailBody;
  };

  // Check if job has already been released
  const releasedStatuses = [
    "Full Release",
    "Engineering Only",
    "Long Lead Only",
    "Release with Qualified Exceptions",
    "Release Pending CSO"
  ];
  
  const isJobAlreadyReleased = releasedStatuses.includes(generalProjectData.JobStatus);

  // If job is already released, show notice instead of form
  if (isJobAlreadyReleased) {
    return (
      <div className="container">
        <h1 className="text-center">RELEASE FOR SCHEDULING FORM</h1>
        <Alert variant="info" className="mt-4">
          <Alert.Heading>Job Already Released</Alert.Heading>
          <hr />
          <p className="mb-2">
            <strong>Job Number:</strong> {generalProjectData.JobNumber || 'N/A'}
          </p>
          <p className="mb-2">
            <strong>Job Name:</strong> {generalProjectData.ProjectName || 'N/A'}
          </p>
          <p className="mb-2">
            <strong>Current Status:</strong> {generalProjectData.JobStatus}
          </p>
          <hr />
          <p className="mb-0">
            This job has already been released to scheduling and cannot be released again using this form.
            If you need to make changes to the release status, please contact the Master Scheduler.
          </p>
        </Alert>
      </div>
    );
  }

  return (
    <div className="container">
      <h1 className="text-center">RELEASE FOR SCHEDULING FORM</h1>
      <Form onSubmit={handleSubmit} onChangeCapture={() => setRealtimeDirty(true)}>
        <fieldset>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Job Name:</Form.Label>
                <Form.Control
                  type="text"
                  name="jobName"
                  value={formData.jobName}
                  onChange={handleChange}
                  disabled
                  required
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Job Number:</Form.Label>
                <Form.Control
                  type="text"
                  name="jobNum"
                  value={formData.jobNum}
                  onChange={handleChange}
                  disabled
                  required
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Model Number:</Form.Label>
                <Form.Control
                  type="text"
                  name="modelNumber"
                  value={formData.modelNumber}
                  onChange={handleChange}
                  disabled
                  required
                />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Released By:</Form.Label>
                <Form.Control
                  type="text"
                  name="releasedBy"
                  value={formData.releasedBy}
                  onChange={handleChange}
                  disabled
                  required
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Salesperson(s) / Account(s) on Job:</Form.Label>
                <Form.Control
                  type="text"
                  name="salesName"
                  value={formData.salesName}
                  onChange={handleChange}
                  required
                />
              </Form.Group>

              {additionalSales.map((_, index) => (
                <Form.Group className="mb-3" key={`additional-sales-${index}`}>
                  <Form.Control
                    type="text"
                    name={`additionalSales-${index}`}
                    value={additionalSales[index]}
                    onChange={(e) => {
                      const updatedSales = [...additionalSales];
                      updatedSales[index] = e.target.value;
                      setAdditionalSales(updatedSales);
                    }}
                    placeholder="Additional Sales Rep"
                  />
                </Form.Group>
              ))}
            </Col>
          </Row>

          <Row>
            <Col md={12}>
              <h5 className="mt-2">Assisting Engineers</h5>
              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Mechanical Engineer:</Form.Label>
                    <Form.Select
                      name="mechEngineer"
                      value={formData.mechEngineer}
                      onChange={handleChange}
                      title="If a mechanical engineer assisted on the pre-sale portion of the project and you believe it would be most efficient for them to work on the production job, include their name here. The Master Scheduler will assign them the job, when practical."
                    >
                      <option value="">Select...</option>
                      {!loading && mechOptions.map((option, index) => (
                        <option key={index} value={option.displayName}>
                          {option.displayName}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Electrical Engineer:</Form.Label>
                    <Form.Select
                      name="elecEngineer"
                      value={formData.elecEngineer}
                      onChange={handleChange}
                      title="If an electrical engineer assisted on the pre-sale portion of the project and you believe it would be most efficient for them to work on the production job, include their name here. The Master Scheduler will assign them the job, when practical."
                    >
                      <option value="">Select...</option>
                      {!loading && elecOptions.map((option, index) => (
                        <option key={index} value={option.displayName}>
                          {option.displayName}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Controls Engineer:</Form.Label>
                    <Form.Select
                      name="controlEngineer"
                      value={formData.controlEngineer}
                      onChange={handleChange}
                      title="If a controls engineer assisted on the pre-sale portion of the project and you believe it would be most efficient for them to work on the production job, include their name here. The Master Scheduler will assign them the job, when practical."
                    >
                      <option value="">Select...</option>
                      {!loading && controlOptions.map((option, index) => (
                        <option key={index} value={option.displayName}>
                          {option.displayName}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
              <p><small>**<i>Assisting engineers are not included in the email distribution. Enter any engineers manually below in the email section.</i></small></p>
            </Col>
          </Row>

          <hr />

          <Row className="justify-content-center">
            <Col md={8}>
              <Form.Group className="mb-3">
                <Form.Label className="fw-bold">What type of release is this? </Form.Label>
                <Button 
                  variant="outline-primary" 
                  size="sm" 
                  className="ms-2"
                  onClick={() => setShowDefinitionsModal(true)}
                >
                  Definitions
                </Button>

                <Modal
                  show={showDefinitionsModal}
                  onHide={() => setShowDefinitionsModal(false)}
                  size="lg"
                  aria-labelledby="definitions-modal"
                  centered
                >
                  <Modal.Header closeButton>
                    <Modal.Title id="definitions-modal">
                      Release Types Definitions
                    </Modal.Title>
                  </Modal.Header>
                  <Modal.Body>
                    <h5>FULL RELEASE:</h5>
                    <ul>
                      <li>We have a signed proposal, and/or PO, and a signed CSO drawing.</li>
                      <li>Engineering should identify and release/order long lead items within 48 hrs.</li>
                      <li>Engineering should design product per Engineering schedule and release job to Production.</li>
                      <li>Fabrication and/or Assembly should commence per Produciton schedule.</li>
                      <li>Ship product per Production schedule.</li>
                    </ul>
                    
                    <h5>FULL RELEASE WITH QUALIFIED EXCEPTIONS:</h5>
                    <ul>
                      <li>Same as a full release but exceptions must be fully qualified for Engineering and/or Production to complete job.</li>
                    </ul>
                    
                    <h5>FULL RELEASE WITH CSO PENDING:</h5>
                    <ul>
                      <li>We have a signed proposal, and/or PO, but do not have a drafted/signed CSO drawing.</li>
                      <li>Engineering will be assigned and scheduled.</li>
                      <li>Job will be added to Jobs Schedule in next available opening.</li>
                      <li>Long leads will normally not be ordered until CSO approval and following a pre-engineering release meeting; however, the option exists, with management approval, to order any special long lead items identified by App/Sales upon release of the job and without CSO approval.</li>
                      <li>Upon release of approved CSO, Application Engineer will issue Change Order and host pre-engineering release meeting.</li>
                    </ul>
                    
                    <h5>ENGINEERING RELEASE ONLY:</h5>
                    <ul>
                      <li>We have a signed proposal, and/or PO, but do not have CSO and/or some required specification.</li>
                      <li>Engineering is assigned and scheduled.</li>
                      <li>Engineering should identify and release/order long lead items within 48 hrs AND complete design per Engineering schedule; but do not release to Production.</li>
                      <li>A change order is required to advance job onto Production schedule.</li>
                    </ul>
                    
                    <h5>LONG LEADS RELEASE ONLY:</h5>
                    <ul>
                      <li>We have a signed proposal, and/or PO, but do not have CSO and/or some required specification.</li>
                      <li>Engineering is assigned but not scheduled.</li>
                      <li>Engineering should identify and release/order long lead items within 48 hrs.</li>
                      <li>A change order is required to advance job onto Engineering and Production schedules.</li>
                    </ul>
                  </Modal.Body>
                  <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDefinitionsModal(false)}>
                      Close
                    </Button>
                  </Modal.Footer>
                </Modal>

                <div className="mt-2">
                  <Form.Check
                    type="radio"
                    id="fullRelease"
                    name="release"
                    label="Full Release"
                    value="Full Release"
                    onChange={handleReleaseChange}
                    required
                  />
                  <Form.Check
                    type="radio"
                    id="releaseExceptions"
                    name="release"
                    label="Full Release with Qualified Exception(s)"
                    value="Full Release with Qualified Exceptions"
                    onChange={handleReleaseChange}
                  />
                  <Form.Check
                    type="radio"
                    id="releaseCSO"
                    name="release"
                    label="Full Release with CSO Pending"
                    value="Full Release with CSO Pending"
                    onChange={handleReleaseChange}
                  />
                  <Form.Check
                    type="radio"
                    id="engRelease"
                    name="release"
                    label="Engineering Release Only"
                    value="Engineering Release Only"
                    onChange={handleReleaseChange}
                  />
                  <Form.Check
                    type="radio"
                    id="longLeadsRelease"
                    name="release"
                    label="Long Leads Release Only"
                    value="Long Leads Release Only"
                    onChange={handleReleaseChange}
                  />
                </div>

                {showExceptions && (
                  <div className="mt-2">
                    <Form.Check
                      type="checkbox"
                      id="exc1"
                      name="exc1"
                      label="RTU Type Missing"
                      checked={formData.exc1}
                      onChange={handleChange}
                    />
                    {showRTUMessage && (
                      <div className="text-danger">
                        <i>Elec. Engineering needs RTU type defined <b>1 week before</b> job is due from Engineering.</i>
                      </div>
                    )}
                    <Form.Check
                      type="checkbox"
                      id="exc2"
                      name="exc2"
                      label="Wet Well Depth Missing"
                      checked={formData.exc2}
                      onChange={handleChange}
                    />
                    <Form.Check
                      type="checkbox"
                      id="exc3"
                      name="exc3"
                      label="Z-Pipe Details Missing"
                      checked={formData.exc3}
                      onChange={handleChange}
                    />
                    <Form.Check
                      type="checkbox"
                      id="exc4"
                      name="exc4"
                      label="Intake / Adaptor Missing"
                      checked={formData.exc4}
                      onChange={handleChange}
                    />
                    <Form.Check
                      type="checkbox"
                      id="exc5"
                      name="exc5"
                      label="WaterVision-Cloud Customer / Site Info Missing"
                      checked={formData.exc5}
                      onChange={handleChange}
                    />
                    {showWVMessage && (
                      <div className="text-danger">
                        <i>Controls Engineering must have this information <b>1 week prior</b> to their due date.</i>
                      </div>
                    )}
                  </div>
                )}

                {showCSODiv && (
                  <div className="mt-2">
                    <Form.Check
                      type="radio"
                      id="CSO1"
                      name="CSOtype"
                      label="Do not order long leads or hold pre-engineering release meeting until CSO approved. A status note shall be added to Jobs Schedule: CSO expected xx/yy; LL not ordered."
                      value="CSO1"
                      onChange={handleCSOTypeChange}
                    />
                    <Form.Check
                      type="radio"
                      id="CSO2"
                      name="CSOtype"
                      label="Only order special long lead items identified in NOTES section of this form, with balance of long leads ordered after CSO approval and pre-engineering release meeting. A status note shall be added to Jobs Schedule: CSO expected xx/yy; special LL ordered."
                      value="CSO2"
                      onChange={handleCSOTypeChange}
                    />
                  </div>
                )}

                {showERDiv && (
                  <div className="mt-2">
                    <Form.Check
                      type="checkbox"
                      id="ER1"
                      name="ER1"
                      label="CSO Missing"
                      checked={formData.ER1}
                      onChange={handleChange}
                    />
                    <Form.Check
                      type="checkbox"
                      id="ER2"
                      name="ER2"
                      label="Other"
                      checked={formData.ER2}
                      onChange={handleChange}
                    />
                    {formData.ER2 && (
                      <Form.Control
                        as="textarea"
                        id="ERother"
                        name="ERother"
                        value={formData.ERother}
                        onChange={handleChange}
                        rows={3}
                        className="mt-2"
                      />
                    )}
                    <Form.Check
                      type="checkbox"
                      id="ER3"
                      name="ER3"
                      label="Long lead items can be ordered without risk of change"
                      checked={formData.ER3}
                      onChange={handleChange}
                      required={showERDiv}
                    />
                  </div>
                )}

                {showLLRDiv && (
                  <div className="mt-2">
                    <Form.Check
                      type="checkbox"
                      id="LLR1"
                      name="LLR1"
                      label="CSO Missing"
                      checked={formData.LLR1}
                      onChange={handleChange}
                    />
                    <Form.Check
                      type="checkbox"
                      id="LLR2"
                      name="LLR2"
                      label="Other"
                      checked={formData.LLR2}
                      onChange={handleChange}
                    />
                    {formData.LLR2 && (
                      <Form.Control
                        as="textarea"
                        id="LLRother"
                        name="LLRother"
                        value={formData.LLRother}
                        onChange={handleChange}
                        rows={3}
                        className="mt-2"
                      />
                    )}
                  </div>
                )}

                {(showCSOAnticipation || showAuthorize) && (
                  <Row className="mt-2">
                    {showCSOAnticipation && (
                      <Col md={showAuthorize ? 6 : 12}>
                        <Form.Group className="mb-3">
                          <Form.Label>CSO approval anticipated by:</Form.Label>
                          <Form.Control
                            type="date"
                            id="CSOdate"
                            name="CSOdate"
                            value={formData.CSOdate}
                            onChange={handleChange}
                            required={showCSOAnticipation}
                          />
                        </Form.Group>
                      </Col>
                    )}
                    
                    {showAuthorize && (
                      <Col md={showCSOAnticipation ? 6 : 12}>
                        <Form.Group className="mb-3">
                          <Form.Label>Manager Authorizing Early Release:</Form.Label>
                          <Form.Select
                            id="manAuthRelease"
                            name="manAuthRelease"
                            value={formData.manAuthRelease}
                            onChange={handleChange}
                            required={showAuthorize}
                          >
                            <option value="">Select...</option>
                            <option value="Chip Carlson|chip.carlson@watertronics.com">Carlson, Chip</option>
                            <option value="Rick Reinders|rick.reinders@watertronics.com">Reinders, Rick</option>
                            <option value="Boyd Rose|boyd.rose@watertronics.com">Rose, Boyd</option>
                          </Form.Select>
                        </Form.Group>
                      </Col>
                    )}
                  </Row>
                )}
                
                {showAuthorize && (
                  <p className="mt-2">
                    <i>NOTE: This is not authorization to move a job up in the Engineering and Production schedules. Requests to move a job up should be reviewed and approved at the morning Production Team meeting.</i>
                  </p>
                )}
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Does job include CSM/HDPE components?</Form.Label>
                <div>
                  <Form.Check
                    inline
                    type="radio"
                    id="YesCSM"
                    name="CSM"
                    label="Yes"
                    value="Yes"
                    checked={formData.CSM === "Yes"}
                    onChange={handleChange}
                    required
                    title="The CSM Team will be included on CO distribution."
                  />
                  <Form.Check
                    inline
                    type="radio"
                    id="NoCSM"
                    name="CSM"
                    label="No"
                    value="No"
                    checked={formData.CSM === "No"}
                    onChange={handleChange}
                  />
                </div>
                <small className="text-muted">
                  The CSM Team will be included on distribution if Yes.
                </small>
              </Form.Group>
            </Col>
          </Row>

          <hr />

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Lead Time:</Form.Label>
                <Form.Select
                  id="leadTime"
                  name="leadTime"
                  value={formData.leadTime}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select...</option>
                  <option value="Standard Lead Time">Standard Lead Time</option>
                  <option value="No Earlier Than Lead Time">No Earlier Than Lead Time</option>
                  <option value="Less Than Standard Lead Time">Less Than Standard Lead Time</option>
                </Form.Select>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Requested Delivery Date:</Form.Label>
                <Form.Control
                  type="date"
                  id="deliveryDate"
                  name="deliveryDate"
                  value={formData.deliveryDate}
                  onChange={handleChange}
                  required
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <div className="mb-3">
                <p><i>
                  <u>Standard Lead Time</u> - if a customer can accept an order at std. lead time then enter the date based on the WT published std. lead time.
                  <br /><br />
                  <u>No Earlier Than Lead Time</u> - if a customer wants an order in the future that does not have to ship within std. lead time then enter that date.
                  <br /><br />
                  <u>Less Than Standard Lead Time</u> - if a customer requires an order to deliver earlier than the std. lead time then enter the date agreed upon in advance with the master scheduler.
                </i></p>
              </div>
            </Col>
          </Row>

          <hr />

          <Form.Group className="mb-3">
            <Form.Label>Notes:</Form.Label>
            <Form.Control
              as="textarea"
              id="notes"
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              rows={7}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Does job include telemetry?</Form.Label>
            <div>
              <Form.Check
                inline
                type="radio"
                id="YesTelemWV"
                name="telemetry"
                label="Yes (WaterVision)"
                value="YesWV"
                checked={formData.telemetry === "YesWV"}
                onChange={handleChange}
                required
              />
              <Form.Check
                inline
                type="radio"
                id="YesTelemFN"
                name="telemetry"
                label="Yes (FieldNet)"
                value="YesFN"
                checked={formData.telemetry === "YesFN"}
                onChange={handleChange}
              />
              <Form.Check
                inline
                type="radio"
                id="NoTelem"
                name="telemetry"
                label="No"
                value="No"
                checked={formData.telemetry === "No"}
                onChange={handleChange}
              />
            </div>
          </Form.Group>

          <hr />
        <Row>
            <Col md={12}>
              <h4>Email Recipients</h4>
              {contactsLoading ? (
                <p>Loading email recipients...</p>
              ) : contactsError ? (
                <p className="text-danger">Error loading email recipients: {contactsError}</p>              ) : (
                <div>
                  <p>The following recipients will receive email notifications:</p>
                  <div className="bg-light p-3 rounded">                    <p className="mb-2">
                      <strong>Default Recipients:</strong> ReleaseGroup@watertronics.com
                      {formData.CSM === 'Yes' && ', NorthPrairieDL@watertronics.com (CSM Team)'}
                      {formData.telemetry === 'YesWV' && ', factoryservice@watertronics.com (Factory Service)'}
                    </p>
                    {projectTeamMembers.length > 0 && (
                      <div>
                        <p className="mb-0">
                          <strong>Project Team:</strong>
                        </p>
                        <ul className="mb-0">
                          {projectTeamMembers.map((member, index) => (
                            <li key={index}>
                              {member.role}: {member.displayName} ({member.email})
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {projectTeamMembers.length === 0 && (
                      <p className="mb-0 text-muted">
                        <em>No project team members found.</em>
                      </p>
                    )}
                  </div>
                </div>
              )}
            </Col>
          </Row>

          <Row>
            <Col md={12}>
              <Form.Group className="mb-3">
                <Form.Label>If necessary, add additional emails:</Form.Label>
                <Form.Control
                  type="text"
                  id="addEmails"
                  name="addEmails"
                  value={formData.addEmails}
                  onChange={handleChange}
                  placeholder="john.doe@email.com; mary.sue@email.com; ..."
                  isInvalid={!!emailError}
                />
                <Form.Control.Feedback type="invalid">
                  {emailError}
                </Form.Control.Feedback>
                <Form.Text className="text-muted">
                  Separate multiple emails with semicolons (;), commas (,), or spaces.
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={12}>
              <Form.Group className="mb-3">
                <Form.Check
                  type="checkbox"
                  id="sendToSelfOnly"
                  name="sendToSelfOnly"
                  label="Only send this email to me"
                  checked={formData.sendToSelfOnly}
                  onChange={handleChange}
                />
              </Form.Group>
            </Col>
          </Row>
        </fieldset>

        <div className="mb-4">
          <p>Upon submission of this form, copies of the release for scheduling form will be emailed to those listed above.</p>
        </div>

        <Button type="submit" variant="primary">Submit</Button>
      </Form>
    </div>
  );
}

export default JobReleaseForm;
